import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

export const giftApi = createApi({
    reducerPath: 'giftApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Gift', 'Category'],
    endpoints: (builder) => ({
        // Get all gifts
        getGifts: builder.query({
            query: (params = {}) => {
                const { page = 0, pageSize = 8, keyword = '', status = '', categoryId = '' } = params;
                let url = `/gift/list?page=${page}&pageSize=${pageSize}`;
                
                if (keyword.trim()) {
                    url += `&keyword=${encodeURIComponent(keyword.trim())}`;
                }
                if (status.trim()) {
                    url += `&status=${encodeURIComponent(status)}`;
                }
                if (categoryId.trim()) {
                    url += `&categoryId=${encodeURIComponent(categoryId)}`;
                }
                
                return {
                    url,
                    method: 'GET'
                };
            },
            transformResponse: (response) => {
                if (!response?.value) {
                    return {
                        gifts: [],
                        totalCount: 0,
                        isSuccess: false
                    };
                }
                return {
                    gifts: response.value.gifts || [],
                    totalCount: response.value.totalCount || 0,
                    isSuccess: response.isSuccess
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'An error occurred',
                errors: response.data?.errors || [],
                validationErrors: response.data?.validationErrors || []
            }),
            providesTags: ['Gift']
        }),

        // Get gift by ID
        getGiftById: builder.query({
            query: (id) => ({
                url: `/gift/${id}`,
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (!response?.value) {
                    return null;
                }
                return {
                    ...response.value,
                    isSuccess: response.isSuccess,
                    errors: response.errors || [],
                    validationErrors: response.validationErrors || []
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Gift not found',
                errors: response.data?.errors || [],
                validationErrors: response.data?.validationErrors || []
            }),
            providesTags: (result, error, id) => [{ type: 'Gift', id }]
        }),

        // Create gift
        createGift: builder.mutation({
            query: (data) => ({
                url: '/gift/create',
                method: 'POST',
                body: {
                    cardName: data.cardName,
                    brand: data.brand,
                    brandId: data.brandId,
                    description: data.description || '',
                    cardImage: data.cardImage,
                    validityStart: data.validityStart,
                    validityEnd: data.validityEnd,
                    maxUsage: data.maxUsage,
                    apiIntegrationCode: data.apiIntegrationCode,
                    categories: data.categories || [],
                    status: data.status,
                    terms: data.terms || '',
                    tenantIds: data.tenantIds || [],
                    metadata: data.metadata || {}
                }
            }),
            transformResponse: (response) => {
                console.log('🔧 GiftAPI CreateGift - Raw Response:', response);
                console.log('📊 Response type:', typeof response);
                console.log('🔍 Response isSuccess:', response?.isSuccess);
                console.log('📋 Response status:', response?.status);
                console.log('⚠️ Response errors:', response?.errors);
                console.log('📝 Response validationErrors:', response?.validationErrors);
                
                const transformedResponse = {
                    ...response,
                    isSuccess: response?.isSuccess || response?.status === 0,
                    errors: response?.errors || [],
                    validationErrors: response?.validationErrors || []
                };
                
                console.log('✅ GiftAPI CreateGift - Transformed Response:', transformedResponse);
                return transformedResponse;
            },
            transformErrorResponse: (response) => {
                console.error('💥 GiftAPI CreateGift - Error Response:', response);
                console.error('📊 Error status:', response.status);
                console.error('📋 Error data:', response.data);
                
                const transformedError = {
                    status: response.status,
                    message: response.data?.message || 'Failed to create gift card',
                    errors: response.data?.errors || ['An unexpected error occurred'],
                    validationErrors: response.data?.validationErrors || []
                };
                
                console.error('🔧 GiftAPI CreateGift - Transformed Error:', transformedError);
                return transformedError;
            },
            invalidatesTags: ['Gift']
        }),

        // Update gift
        updateGift: builder.mutation({
            query: (data) => ({
                url: '/gift/update',
                method: 'PUT',
                body: {
                    id: data.id,
                    cardName: data.cardName,
                    brand: data.brand,
                    brandId: data.brandId,
                    description: data.description || '',
                    cardImage: data.cardImage,
                    validityStart: data.validityStart,
                    validityEnd: data.validityEnd,
                    maxUsage: data.maxUsage,
                    apiIntegrationCode: data.apiIntegrationCode,
                    categories: data.categories || [],
                    status: data.status,
                    terms: data.terms || '',
                    tenantIds: data.tenantIds || [],
                    metadata: data.metadata || {}
                }
            }),
            transformResponse: (response) => {
                console.log('🔧 GiftAPI UpdateGift - Raw Response:', response);
                console.log('📊 Response type:', typeof response);
                console.log('🔍 Response isSuccess:', response?.isSuccess);
                console.log('📋 Response status:', response?.status);
                console.log('⚠️ Response errors:', response?.errors);
                console.log('📝 Response validationErrors:', response?.validationErrors);
                
                const transformedResponse = {
                    ...response,
                    isSuccess: response?.isSuccess || response?.status === 0,
                    errors: response?.errors || [],
                    validationErrors: response?.validationErrors || []
                };
                
                console.log('✅ GiftAPI UpdateGift - Transformed Response:', transformedResponse);
                return transformedResponse;
            },
            transformErrorResponse: (response) => {
                console.error('💥 GiftAPI UpdateGift - Error Response:', response);
                console.error('📊 Error status:', response.status);
                console.error('📋 Error data:', response.data);
                
                const transformedError = {
                    status: response.status,
                    message: response.data?.message || 'Failed to update gift card',
                    errors: response.data?.errors || ['An unexpected error occurred'],
                    validationErrors: response.data?.validationErrors || []
                };
                
                console.error('🔧 GiftAPI UpdateGift - Transformed Error:', transformedError);
                return transformedError;
            },
            invalidatesTags: ['Gift']
        }),

        // Delete gift
        deleteGift: builder.mutation({
            query: (id) => ({
                url: `/gift/${id}`,
                method: 'DELETE'
            }),
            transformResponse: (response) => ({
                ...response,
                isSuccess: response?.isSuccess || response?.status === 0,
                errors: response?.errors || [],
                validationErrors: response?.validationErrors || []
            }),
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to delete gift card',
                errors: response.data?.errors || []
            }),
            invalidatesTags: ['Gift']
        }),

        // Upload gift media
        uploadGiftMedia: builder.mutation({
            query: ({ orderNo, file }) => {
                const formData = new FormData();
                formData.append('orderNo', orderNo);
                formData.append('file', file);
                return {
                    url: '/gift/media',
                    method: 'POST',
                    body: formData,
                    formData: true
                };
            },
            transformResponse: (response) => ({
                ...response,
                isSuccess: response?.isSuccess || response?.status === 0,
                errors: response?.errors || [],
                validationErrors: response?.validationErrors || [],
                url: response?.value?.url || response?.url
            }),
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to upload image',
                errors: response.data?.errors || ['Upload failed']
            })
        })
    })
});

export const {
    useGetGiftsQuery,
    useGetGiftByIdQuery,
    useCreateGiftMutation,
    useUpdateGiftMutation,
    useDeleteGiftMutation,
    useUploadGiftMediaMutation
} = giftApi; 